import {
    Button,
    Card,
    CardActions,
    CardContent,
    colors,
    Grid,
    Modal,
    TextField,
    Theme,
    Typography
} from '@material-ui/core';
import {makeStyles} from '@material-ui/styles';
import clsx from 'clsx';
import {useSnackbar} from 'notistack';
import React, {useEffect, useState} from 'react';
import {LoadingOverlay} from '../../../components/LoadingOverlay';
import addLog from '../../../graphql/addLog.api';
import {getMe} from '../../../graphql/auth';
import {CommunicationType, CommunicationTypes} from '../../../graphql/comms.datatypes';
import logCodes from '../../../graphql/logCodes.api';
import {WcvLogCodes, WcvLogLevel, WcvType} from '../../../graphql/logs.datatypes';
import anyLoading from '../../../utils/anyLoading';
import {getHandleFieldChange, getHasError} from '../../../utils/formHelpers';
import validate from 'validate.js';
import getEmails from '../../EmailManagement/getEmails.api';

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        outline: 'none',
        boxShadow: theme.shadows[20],
        width: 700,
        maxHeight: '90%',
        overflowY: 'auto',
        maxWidth: '95%'
    },
    container: {
        marginTop: theme.spacing(3)
    },
    actions: {
        justifyContent: 'flex-end'
    },
    saveButton: {
        color: theme.palette.white,
        backgroundColor: colors.green[600],
        '&:hover': {
            backgroundColor: colors.green[900]
        }
    }
}));

const UsersSendMessage = (props: {
    className?: string,
    userIds: number[],
    onClose: () => void,
    open: boolean
}) => {
    const {open, onClose, userIds, className, ...rest} = props;
    const classes = useStyles();
    const meData = getMe();
    const logCodesData = logCodes();
    const [sendAddLog, addLogData] = addLog(false);
    const loadingData = anyLoading(meData, logCodesData, addLogData);
    const {enqueueSnackbar} = useSnackbar();
    const [formState, setFormState] = useState({
        title: '',
        body: '',
        from: '',
        user_ids: userIds,
        communication_type: CommunicationType.EMAIL,
    });
    const emailData = getEmails();
    const emails = emailData && emailData.data && emailData.data.emails || [];

    //tours
    const [errors, setErrors] = useState({
        isValid: false,
        errors: {
            title: false,
            from: false,
            body: false,
        }
    });

    //2. Setup helper functions for form
    const handleFieldChange = getHandleFieldChange(setFormState);
    const hasError = getHasError(errors);

    //3. setup validation and error hook
    const validateSchema: any = {
        title: {
            presence: {allowEmpty: false, message: 'is required'},
        },
        from: {
            presence: {allowEmpty: false, message: 'is required'},
            email: true,
            length: {
                maximum: 64
            }
        },
        body: {
            presence: {allowEmpty: false, message: 'is required'},
        },
    };

    useEffect(() => {
        if (formState.communication_type === CommunicationType.SMS) {
            validateSchema.title = undefined;
            validateSchema.from = undefined;
        }

        const newErrors = validate(formState, validateSchema);

        setErrors(state => ({
            ...state,
            isValid: !newErrors,
            errors: newErrors || {}
        }));
    }, [formState]);


    const onSendClick = () => {
        const logCode = [
            WcvLogCodes.COMMUNICATION_SEND_EMAIL,
            WcvLogCodes.COMMUNICATION_SEND_SMS,
        ];

        const sanitisedData = {
            data: {
                level: WcvLogLevel.INFO,
                user_id: meData!.data!.me!.id,
                type: WcvType.COMMUNICATION,
                type_id: null,
                code: logCode[formState.communication_type],
                description: 'Send message to users.',
                payload: JSON.stringify({
                    title: formState.title,
                    body: formState.body,
                    from: formState.from,
                    user_ids: userIds,
                }),
            }
        };

        sendAddLog({variables: sanitisedData})
            .then(() => {
                enqueueSnackbar('Message Sent', {variant: 'success'});
                onClose();
            })
            .catch(() => {
                enqueueSnackbar('Message Failed.', {variant: 'error'});
            });
    };

    if (!open) {
        return null;
    }

    return (
        <Modal onClose={onClose} open={open}>
            <Card {...rest} className={clsx(classes.root, className)}>
                <LoadingOverlay loading={loadingData.loading}>
                    <form>
                        <CardContent>
                            <Typography gutterBottom variant="h3">
                                Send Message:
                            </Typography>
                            <Grid className={classes.container} container spacing={3}>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Select Type"
                                        name="communication_type"
                                        onChange={handleFieldChange}
                                        select
                                        // eslint-disable-next-line react/jsx-sort-props
                                        SelectProps={{native: true}}
                                        value={String(formState.communication_type)}
                                        variant="outlined"
                                    >
                                        {CommunicationTypes.map((type, i) => (
                                            <option
                                                key={type.name}
                                                value={i}
                                            >
                                                {type.name}
                                            </option>
                                        ))}
                                    </TextField>
                                </Grid>


                                {formState.communication_type === CommunicationType.EMAIL && (
                                    <Grid item xs={12}>
                                        <TextField
                                            {...hasError('title')}
                                            fullWidth label="Title" name="title" onChange={handleFieldChange}
                                            value={formState.title}
                                            variant="outlined"/>
                                    </Grid>
                                )}
                                {formState.communication_type === CommunicationType.EMAIL && (
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="From"
                                            name="from"
                                            {...hasError('from')}
                                            onChange={handleFieldChange}
                                            select
                                            // eslint-disable-next-line react/jsx-sort-props
                                            SelectProps={{native: true}}
                                            InputLabelProps={{shrink: true}}
                                            value={String(formState.from)}
                                            variant="outlined"
                                        >
                                            <option>please select...</option>
                                            {emails.map((email, i) => (
                                                <option key={i} value={email.email}>
                                                    {email.email}
                                                </option>
                                            ))}
                                        </TextField>
                                    </Grid>
                                )}
                                <Grid item xs={12}>
                                    <TextField fullWidth label="Body" name="body" multiline rows={3}
                                               {...hasError('body')}
                                               onChange={handleFieldChange}
                                               value={formState.body} variant="outlined"/>
                                </Grid>

                            </Grid>
                        </CardContent>
                        <CardActions className={classes.actions}>
                            <Button onClick={onClose} variant="contained">Close</Button>
                            <Button className={classes.saveButton} onClick={onSendClick} disabled={!errors.isValid}
                                    variant="contained">Send</Button>
                        </CardActions>
                    </form>
                </LoadingOverlay>
            </Card>
        </Modal>
    );
};

export default UsersSendMessage;
